import React, { PropTypes } from 'react';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';
import { get, merge, omit, reduce, intersection } from 'lodash';
import { mapFormFields, reduceToValues, updateFormFields, fieldsHasErrors, validateForm } from '../../../lib/form';
import Button from '../../../common/components/Button';
import ToggleButton from '../../../common/components/c_ToggleButton';
import TextField from '../../../common/components/c_TextField';
import TextArea from '../../../common/components/c_TextArea';
import DropDownMenu from '../../../common/components/c_DropDownMenu';
import CloseX from '../../../common/svgs/CloseX';

import { lookup } from '../../../redux/helpers/staticData';
import * as companyApi from '../../../redux/actionCreators/async/companies';
import * as ui from '../../../redux/actionCreators/sync/ui';
import * as syncActions from '../../../redux/actionCreators/sync';
import { withRouter } from 'react-router';

import cx from 'classnames';

const yesNo = [{ value: true, primaryText: 'Yes' }, { value: false, primaryText: 'No' }];
const vendorProprietary = [{ value: true, primaryText: 'Vendor' }, { value: false, primaryText: 'Proprietary' }];

const techFields = {
  required: [],
  optional: ['additionalInfo', 'internalUseOnly', 'vendorName', 'vendorVersion', 'vendorLocation', 'vendorAgencyCode',
             'phoneProvider', 'remoteConnectivityOther', 'hasVolumeLicensing', 'hasRemoteExtensions',
             'remoteExtensionType', 'remoteExtensionTypeOther', 'needAssistance', 'remoteConnectivity', 'remoteVendor',
             'ipOrHostname', 'isVendorSystem', 'hasWahveExtension', 'requiresMSOffice', 'webConfSoftwareId',
             'webConfSoftwareOther', 'mfaLogin']
};

@connect((state, props) => ({
  contact: state.contactProfile,
  staticData: state.staticData,
  companyId: parseInt(props.params.companyId, 10) || 0,
  isAdmin: intersection(['Admin', 'Employee'], state.user.roles).length ? true : false,
  isInternalUseEnabled: state.user.privs.includes('ENABLE_INTERNAL_IT_NOTES'),
  labels: get(state, 'labels.Client.Company.ClientTechQA', {}),
  webConfSoftwareOther: get(state, 'staticData.webConfSoftware', []).find(software => software.hardcodelink === 'OTHER')
}))
class TechQA extends React.Component {

  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    contact: PropTypes.object.isRequired,
    staticData: PropTypes.object,
    params: PropTypes.object,
    isAdmin: PropTypes.bool,
    isInternalUseEnabled: PropTypes.bool,
    labels: PropTypes.object,
    webConfSoftwareOther: PropTypes.object.isRequired
  }

  constructor(props, context) {
    super(props, context);

    this.state = {
      showErrors: false,
      tech: null,
      saveDisabled: true
    };
  }


  componentDidMount() {
    this.loadTech(this.props);
    this.props.dispatch(syncActions.setNavState({ isOpen: true, hideClose: true, isDisabled: true }));

    this.props.router.setRouteLeaveHook(this.props.route, () => {
      this.props.dispatch(syncActions.setNavState({ isOpen: true, hideClose: false, isDisabled: false }));
    });
  }


  componentWillReceiveProps(nextProps) {
    this.loadTech(nextProps);
  }

  componentWillUnmount() {
    this.props.dispatch(syncActions.setNavState({ isOpen: true, hideClose: false, isDisabled: false }));
  }

  loadTech(props) {
    const companyId = this.props.companyId || get(props.contact, 'company.id', null);
    if (companyId && !this.state.tech) {
      props.dispatch(companyApi.getCompanyTech(companyId, (err, data) => {
        if (err === null && data !== null) {
          // load tech and load form data once that's loaded
          this.setState({ tech: data.items[0] || {} }, () => this.setState({ techForm: mapFormFields(this.state.tech, techFields.required, techFields.optional) }));
        }
      }));
    }
  }

  handleCancel() {
    this.props.dispatch(syncActions.setNavState({ isOpen: true, hideClose: false, isDisabled: false }));
    const path = this.props.isAdmin ? `/admin/companies/${this.props.companyId}/details/` : '/client/company';
    this.props.dispatch(push(path));
  }

  handleSave() {
    validateForm(this.state.techForm, techFields);
    const showErrors = this.hasErrors(this.state.techForm);
    if (showErrors) {
      return this.setState(merge({}, { ...this.state }, { showErrors }));
    }

    const updatedTech = reduce(this.state.techForm, (form, model, prop) => {
      const newModel = {};
      newModel[prop] = model;
      form = merge(form, reduceToValues(newModel));
      return form;
    }, {});

    updatedTech.companyId = this.props.companyId || get(this.props.contact, 'company.id', null);

    let techFunc = 'createCompanyTech';
    if (get(this.state.tech, 'id', null)) {
      techFunc = 'updateCompanyTech';
    }

    this.props.dispatch(companyApi[techFunc](updatedTech, (err) => {

      if (err) {
        console.error(`${techFunc} failed.`);
        return;
      }

      this.props.dispatch(ui.showSnackBar());

      if (this.props.companyId === 0) {
        this.props.dispatch(push('/client/company'));
      }

    }));
    this.setState(merge({}, { ...this.state }, { saveDisabled: true }));
  }

  hasErrors(form) {
    return reduce(form, (errors, model, prop) => {
      const newModel = {};
      newModel[prop] = model;
      return fieldsHasErrors(newModel) || errors;
    },
      false
    );
  }

  checkValue(field, value) {
    return get(this.state.techForm, `${field}.value`, null) === value ||
      (get(this.state.techForm, `${field}.defaultValue`, null) === value &&
        this.state.techForm[field].value === undefined);
  }

  handleChange(event, prop, value) {
    const techForm = get(merge({}, { ...this.state }), 'techForm');
    updateFormFields(techForm, prop, value === undefined || value === null ? event.target.value : value);
    const showErrors = this.hasErrors(techForm);
    this.setState(merge({}, omit(this.state, ['techForm']), { techForm, showErrors, saveDisabled: false }));
  }

  renderFieldProperties(prop, label) {
    const field = get(this.state.techForm, `${prop}`, {});

    return {
      'data-id': `${prop}`,
      defaultValue: field.defaultValue,
      value: field.value === undefined ? field.defaultValue : field.value,
      floatingLabelText: label,
      floatingLabelFixed: true,
      errorText: field.required ? get(field, 'errors[0]') : undefined,
      onChange: (event, value) => this.handleChange(event, prop, value),
    };
  }

  renderDropDownFieldProperties(prop, label, menuItems) {
    return merge(this.renderFieldProperties(prop, label), { menuItems });
  }

  render() {
    return (
      <div className={cx('tech-qa', { admin: this.props.isAdmin })}>
        <h3 className='close-button hover-link' data-id='close' onClick={() => this.handleCancel()}>CLOSE <CloseX style={{ color: '#000' }} /></h3>
        <div className='grid tech-qa-inner-container'>
          {this.props.companyId === 0
            &&
            <h1>Technology Questions</h1>
          }
          <h2>Remote Workstations Specifications</h2>

          <div className='box-container-fixed'>
            <div className='box'>
              <ToggleButton
                menuItems={yesNo}
                {...this.renderFieldProperties('needAssistance', `${this.props.labels.needAssistance || ''}`)} />
            </div>
            <div className='box'>
              <DropDownMenu
                {...this.renderDropDownFieldProperties('remoteConnectivity', 'Please select the methodology you use for remote connectivity', this.props.staticData.remoteConnectivityTypes)}
              />
            </div>
            {
              this.checkValue('remoteConnectivity', 4)
              &&
              <div className='box'>
                <TextField
                  {...this.renderFieldProperties('remoteConnectivityOther', 'Other')}
                />
              </div>
            }

            <div className='box'>
              <TextField
                {...this.renderFieldProperties('remoteVendor', `${lookup(this.props.staticData, 'remoteConnectivityTypes', { id: get(this.state.techForm, 'remoteConnectivity.value', 0) }) || ''} Vendor: (e.g. Cisco, Microsoft)`)}
              />
            </div>
            <div className='box'>
              <TextField  {...this.renderFieldProperties('ipOrHostname', 'Host Name / IP')} />
            </div>
          </div>


          <h2>Management System</h2>

          <div className='box-container-fixed'>

            <div className='box'>
              <ToggleButton
                menuItems={vendorProprietary}
                {...this.renderFieldProperties('isVendorSystem', 'Do you use a vendor or proprietary management system?')}
              />
            </div>
            {
              this.checkValue('isVendorSystem', true)
              &&
              <div className='box'>
                <TextField {...this.renderFieldProperties('vendorName', 'Software Vendor Name')} />
              </div>
            }

            {
              this.checkValue('isVendorSystem', true)
              &&
              <div className='box'>
                <TextField {...this.renderFieldProperties('vendorVersion', 'Software Version')} />
              </div>
            }
            {
              this.checkValue('isVendorSystem', true)
              &&
              <div className='box'>
                <TextField {...this.renderFieldProperties('vendorLocation', 'Online Web Server Location')} />
              </div>
            }

            {
              this.checkValue('isVendorSystem', true)
              &&
              <div className='box'>
                <TextField  {...this.renderFieldProperties('vendorAgencyCode', 'Agency Code (If Applicable)')} />
              </div>
            }
          </div>

          <h2>Phone System</h2>
          <div className='box-container-fixed'>
            <div className='box'>
              <ToggleButton
                menuItems={yesNo}
                {...this.renderFieldProperties('hasWahveExtension', this.props.labels.hasWahveExtension)}
              />
            </div>
            <div className='box'>{this.props.labels.note}</div>
            {
              this.checkValue('hasWahveExtension', true)
              &&
              <div className='box'>
                <ToggleButton
                  menuItems={yesNo}
                  {...this.renderFieldProperties('hasRemoteExtensions', 'Can your system support remote extensions?')} />
              </div>
            }
            {
              this.checkValue('hasWahveExtension', true)
              &&
              <div className='box'>
                <DropDownMenu {...this.renderDropDownFieldProperties('remoteExtensionType', 'What method will you use for the remote extension?', this.props.staticData.remoteExtensionTypes)} />
              </div>
            }
            {
              this.checkValue('hasWahveExtension', true)
              &&
              this.checkValue('remoteExtensionType', 5)
              &&
              <div className='box'>
                <TextField
                  {...this.renderFieldProperties('remoteExtensionTypeOther', 'Other')}
                />
              </div>
            }
            {
              this.checkValue('hasWahveExtension', true)
              &&
              <div className='box'>
                <TextField {...this.renderFieldProperties('phoneProvider', 'Who is your phone system provider?')} />
              </div>
            }
          </div>

          <h2>Software and Additional Information</h2>

          <div className='box-container-fixed'>
            <div className='box'>
              <DropDownMenu {...this.renderDropDownFieldProperties(
                'webConfSoftwareId',
                this.props.labels.webConfSoftware,
                this.props.staticData.webConfSoftware
              )} />
            </div>
            {
              this.checkValue('webConfSoftwareId', this.props.webConfSoftwareOther.id)
              &&
              <div className='box'>
                <TextField  {...this.renderFieldProperties('webConfSoftwareOther', this.props.labels.webConfSoftwareOther)} />
              </div>
            }

            <div className='box'>
              <ToggleButton
                menuItems={yesNo}
                {...this.renderFieldProperties('requiresMSOffice', 'Does your setup require Microsoft Office?')}
              />
            </div>
            {
              this.checkValue('requiresMSOffice', true)
              &&
              <div className='box'>
                <ToggleButton
                  menuItems={yesNo}
                  {...this.renderFieldProperties('hasVolumeLicensing', this.props.labels.hasVolumeLicensing)}
                />
              </div>
            }

            <div className='box'>
              <ToggleButton
                menuItems={yesNo}
                {...this.renderFieldProperties('mfaLogin', this.props.labels.mfaLogin)}
              />
            </div>

          </div>
          <div className='row'>
            <div className='col'>
              <TextArea
                description={this.props.labels.additionalInfo}
                {...this.renderFieldProperties('additionalInfo', this.props.labels.additionalInfo)} />
            </div>
          </div>

          {
            this.props.isInternalUseEnabled &&
            <div className='row'>
              <div className='col'>
                <TextArea
                  description={this.props.labels.internalUseOnly}
                  {...this.renderFieldProperties('internalUseOnly', this.props.labels.internalUseOnly)} />
              </div>
            </div>
          }

          <ul className='row'>
            <li className='col'>
              {this.state.showErrors && <div className='error-message'>Please fill out missing fields.</div>}
            </li>
            {this.props.companyId === 0
              &&
              <li className='col'>
                <Button
                  className='button-secondary-large'
                  style={{ width: '100%' }}
                  label='Cancel'
                  onClick={(e) => this.handleCancel(e)}
                />
              </li>
            }
            <li className='col'>
              <Button
                disabled={this.state.saveDisabled}
                className='button-main-large'
                style={{ width: '100%' }}
                label='Save'
                onClick={(e) => this.handleSave(e)}
              />
            </li>
          </ul>
        </div>
      </div>
    );
  }
}

export default withRouter(TechQA);



// WEBPACK FOOTER //
// ./shared/react/client/TechQA/index.js